import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/** Bordered page rail used by the landing page's section layouts. */
export function Rail({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("relative mx-auto w-full max-w-7xl border-x border-border", className)}>
      {children}
    </div>
  );
}

/** Simple square marker for intersections in the page's hairline grid. */
export function KiteMark({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cn("pointer-events-none absolute size-[13px] rounded-[3px] border border-border bg-background", className)}
    />
  );
}

export function Corners({ className }: { className?: string }) {
  return (
    <div aria-hidden="true" className={cn("pointer-events-none absolute inset-0", className)}>
      <KiteMark className="-left-[6px] -top-[6px]" />
      <KiteMark className="-right-[6px] -top-[6px]" />
      <KiteMark className="-left-[6px] -bottom-[6px]" />
      <KiteMark className="-right-[6px] -bottom-[6px]" />
    </div>
  );
}

export function Eyebrow({ index, children, className }: { index?: string; children: ReactNode; className?: string }) {
  return (
    <div className={cn("flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground", className)}>
      {index && <span className="text-signal">[{index}]</span>}
      <span>{children}</span>
    </div>
  );
}

export function SectionHeader({
  index,
  label,
  title,
  description,
  className,
}: {
  index: string;
  label: string;
  title: ReactNode;
  description?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("grid gap-6 border-b border-border px-6 py-10 lg:grid-cols-12 lg:px-10 lg:py-14", className)}>
      <div className="lg:col-span-3">
        <Eyebrow index={index}>{label}</Eyebrow>
      </div>
      <div className="lg:col-span-9">
        <h2 className="text-balance font-display text-3xl leading-[1.05] md:text-4xl lg:text-5xl">{title}</h2>
        {description && <p className="mt-5 max-w-2xl text-pretty text-sm leading-relaxed text-muted-foreground md:text-base">{description}</p>}
      </div>
    </div>
  );
}

type SignalTone = "high" | "medium" | "low";

const signalToneClass: Record<SignalTone, string> = {
  high: "border-foreground bg-foreground text-background",
  medium: "border-foreground/20 bg-foreground/[0.06] text-foreground/80",
  low: "border-border text-muted-foreground",
};

export function SignalBadge({ tone, className }: { tone: SignalTone; className?: string }) {
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-sm border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider", signalToneClass[tone], className)}>
      <span className={cn("size-1.5 rounded-full", tone === "high" ? "bg-background" : tone === "medium" ? "bg-foreground/60" : "bg-muted-foreground/60")} />
      {tone}
    </span>
  );
}

export function SignalDot({ tone, className, title }: { tone: SignalTone; className?: string; title?: string }) {
  return (
    <span
      aria-label={title ?? `${tone} signal`}
      title={title}
      className={cn(
        "inline-block size-2 shrink-0 rounded-full",
        tone === "high" ? "bg-red-500" : tone === "medium" ? "bg-amber-500" : "bg-zinc-400",
        className,
      )}
    />
  );
}

export function PrimaryLink({ href, children, className }: { href: string; children: ReactNode; className?: string }) {
  return (
    <a href={href} className={cn("inline-flex h-8 items-center gap-1.5 rounded-md bg-foreground px-3 font-mono text-[11px] uppercase tracking-[0.08em] text-background transition-opacity hover:opacity-85 active:scale-[0.97]", className)}>
      {children}
    </a>
  );
}

export function GhostLink({ href, children, className }: { href: string; children: ReactNode; className?: string }) {
  return (
    <a href={href} className={cn("inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-3 font-mono text-[11px] uppercase tracking-[0.08em] text-foreground transition-colors hover:border-foreground/25 hover:bg-foreground/5 active:scale-[0.97]", className)}>
      {children}
    </a>
  );
}